import { useState } from 'react';
import { MenuItem, Stack, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { TextField } from '@theguarantors/tg-package-material-ui';

import { useScreenWidth } from '@/hooks';
import { createDataTestId } from '@/utils/createDataTestId';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';

type BaseSelectOption = {
  id: string;
  value: string;
  label: string;
};

export type LeaseDetailsSelectProps<T extends BaseSelectOption> = {
  name: string;
  title: string;
  helperText?: React.ReactNode;
  options: T[];
  defaultValue?: string;
  onClose?: () => void;
  SelectItem: React.ComponentType<SelectItemProps<T>>;
  required?: boolean;
  onSelect?: (value: string) => void;
  disabled?: boolean;
  emptyOption: T;
};

type SelectItemProps<T> = {
  option: T;
  open: boolean;
};

const getMenuProps = (isDesktop: boolean) => {
  if (isDesktop) {
    return {};
  }

  return {
    anchorOrigin: {
      horizontal: 'center',
      vertical: 'top',
    },
    transformOrigin: {
      horizontal: 'center',
      vertical: 'bottom',
    },
  } as const;
};

export const LeaseDetailsSelect = <T extends BaseSelectOption>({
  name,
  title,
  helperText,
  options,
  defaultValue = '',
  onClose,
  SelectItem,
  required = false,
  onSelect,
  disabled = false,
  emptyOption,
}: LeaseDetailsSelectProps<T>) => {
  const { watch, setValue, trigger, getFieldState, formState } = useFormContext();
  const { isDesktop } = useScreenWidth();
  const { inputSize } = useLeaseDetailsUI();
  const [isSelecting, setIsSelecting] = useState(false);

  const value = (watch(name) as string | undefined) || defaultValue;
  // `getFieldState` walks dotted paths (e.g. `applicants.0.coverage`) where a
  // bare `formState.errors[name]` lookup would return undefined.
  const error = getFieldState(name, formState).error;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: nextValue } = event?.target ?? {};
    if (nextValue == null) return;

    onSelect?.(nextValue);
    setValue(name, nextValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
    trigger(name);
  };

  const handleClose = () => {
    setIsSelecting(false);
    trigger(name);
    onClose?.();
  };

  const handleOpen = () => {
    setIsSelecting(true);
  };

  const menuProps = getMenuProps(isDesktop);

  const dataTestId = createDataTestId({ inputString: name, suffix: 'select' });

  return (
    <Stack direction="column" spacing={1}>
      <TextField
        data-testid={dataTestId}
        color="secondary"
        disabled={disabled}
        error={!!error?.message}
        helperText={error?.message?.toString()}
        label={title}
        name={name}
        onChange={handleChange}
        select
        required={required}
        size={inputSize}
        slotProps={{
          inputLabel: disabled ? { shrink: false } : undefined,
          select: {
            onClose: handleClose,
            onOpen: handleOpen,
            required,
            renderValue: (selected: unknown) => {
              if (disabled) {
                return '';
              }

              const selectedOption = options.find(({ value }) => value === selected);
              const currentlySelected = selectedOption || emptyOption;

              if (isSelecting) {
                return currentlySelected.label;
              }

              return <SelectItem option={currentlySelected} open={false} />;
            },
            MenuProps: {
              ...menuProps,
              slotProps: {
                backdrop: {
                  invisible: true,
                  sx: {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
          },
        }}
        sx={{ width: '100%' }}
        value={value}
      >
        {options.map((option) => (
          <MenuItem
            key={option.id}
            title={option.label}
            value={option.value}
            sx={{
              padding: '8px 12px',
            }}
          >
            <SelectItem option={option} open={isSelecting} />
          </MenuItem>
        ))}
      </TextField>
      {helperText && (
        <Typography variant="body2" sx={{ color: 'grey.600' }}>
          {helperText}
        </Typography>
      )}
    </Stack>
  );
};
